import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export const TermsPage = () => {
  return (
    <div className="min-h-screen bg-dark-primary py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-purple-light hover:text-purple-primary transition-colors mb-8">
          <ArrowLeft className="mr-2" size={20} />
          Voltar para Home
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold font-archive text-white mb-4 glow-text">
            Termos e Políticas
          </h1>
          <p className="text-xl text-gray-400 font-hero">
            Última atualização: 28/06/2025
          </p>
        </div>

        <Tabs defaultValue="terms" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-dark-secondary mb-8">
            <TabsTrigger value="terms" className="data-[state=active]:bg-purple-primary">
              <FileText className="mr-2" size={18} />
              Termo de Uso
            </TabsTrigger>
            <TabsTrigger value="privacy" className="data-[state=active]:bg-purple-primary">
              <Shield className="mr-2" size={18} />
              Política de Privacidade
            </TabsTrigger>
          </TabsList>

          <TabsContent value="terms" className="bg-dark-secondary border border-purple-primary/20 rounded-xl p-8 space-y-8">
            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">1. INTRODUÇÃO</h2>
              <p className="text-gray-300 font-hero leading-relaxed">
                Este Termo de Uso regula o uso da plataforma Gameodys, um ambiente digital de entretenimento que promove partidas competitivas entre jogadores em jogos como Truco Paulista, Truco Mineiro, Sinuca Tradicional e Sinuca 9 Bolas. A Gameodys oferece modos gratuitos e competitivos baseados em habilidade, com premiações definidas por desempenho conforme regras internas. Esta plataforma é destinada a públicos de todas as idades, sendo o acesso por menores de idade condicionado à supervisão e autorização de seus responsáveis legais.
              </p>
              <p className="text-gray-300 font-hero leading-relaxed mt-4">
                O uso da plataforma implica a aceitação integral e irrestrita destes Termos de Uso, bem como da Política de Privacidade. Os termos poderão ser atualizados periodicamente.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">2. CONDIÇÕES DE USO</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>2.1. O uso é permitido a usuários de todas as idades. Menores de 18 anos devem estar supervisionados por seus responsáveis legais, que assumem total responsabilidade pelo uso.</li>
                <li>2.2. O cadastro exige dados verdadeiros e atualizados. A falsidade de informações poderá acarretar em penalidades, inclusive o banimento.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">3. FUNCIONALIDADE DA PLATAFORMA</h2>
              <p className="text-gray-300 font-hero leading-relaxed mb-3">
                3.1. A Gameodys disponibiliza jogos digitais baseados em habilidade. Os modos disponíveis incluem:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero ml-6 mb-4">
                <li>Jogo gratuito;</li>
                <li>Jogo competitivo com GameCoins (moeda gratuita);</li>
                <li>Jogo competitivo com Créditos (moeda vinculada a valor real).</li>
              </ul>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>3.2. Os GameCoins não constituem moeda real, não geram juros e não têm valor fora da plataforma.</li>
                <li>3.3. Em partidas competitivas, recompensas podem ser concedidas com base em critérios objetivos e transparentes de desempenho.</li>
                <li>3.4. Todos os jogadores devem aceitar as regras específicas de cada modalidade antes de iniciar uma partida.</li>
                <li>3.5. Caso sejam detectados erros nas partidas, elas serão canceladas e os respectivos valores retornarão para os saldos dos jogadores, exceto em situações em que forem detectadas atitudes que infrinjam as regras da Gameodys. Tais situações serão analisadas separadamente.</li>
                <li>3.6. A plataforma também poderá oferecer itens cosméticos como skins, molduras ou mesas personalizadas, disponíveis para compra com GameCoins ou Créditos, conforme disponibilidade e regras da plataforma.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">4. TRANSAÇÕES E RECOMPENSAS</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>4.1. O uso de Créditos pode ser feito mediante aquisição via métodos autorizados ou recebimento interno por desempenho.</li>
                <li>4.2. Todas as transações são registradas e auditáveis.</li>
                <li>4.3. A Gameodys pode cobrar taxas de serviço sobre certas funcionalidades, comunicadas previamente.</li>
                <li>4.4. Os valores acumulados em recompensas podem ser retirados mediante verificação de identidade, observando prazos e condições descritas nas regras operacionais.</li>
                <li>4.5. O usuário é o único responsável por relatar seus ganhos e perdas, se exigido por qualquer lei, regulamento ou autoridade relevante (incluindo autoridade fiscal), e por pagar quaisquer impostos que possam ser devidos sobre quaisquer fundos ou prêmios concedidos através do uso dos serviços. A Gameodys não fornece aconselhamento legal ou fiscal, devendo o usuário buscar orientação com profissionais qualificados.</li>
                <li>4.6. O saldo financeiro do usuário será disponibilizado em sua área pessoal sob o nome "Saldo Gameodys" ou "Créditos Gameodys", que funcionará como uma carteira virtual interna da plataforma. Este saldo poderá ser utilizado exclusivamente para fins relacionados ao uso dos serviços da Gameodys e estará sujeito às regras deste Termo.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">5. CONDUTA DO USUÁRIO</h2>
              <p className="text-gray-300 font-hero leading-relaxed mb-3">5.1. É proibido:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero ml-6 mb-4">
                <li>Usar hacks, bots, scripts ou qualquer meio de trapaça;</li>
                <li>Manipular resultados;</li>
                <li>Utilizar múltiplas contas para obter vantagens;</li>
                <li>Cometer atitudes antidesportivas.</li>
              </ul>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>5.2. Penalidades variam de advertência a banimento definitivo.</li>
                <li>5.3. Usuários banidos por fraude ou violação grave perdem o direito de reembolso de valores acumulados.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">6. SEGURANÇA E PRIVACIDADE</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>6.1. Todos os dados são tratados conforme a LGPD (Lei nº 13.709/2018).</li>
                <li>6.2. Informações bancárias são criptografadas e utilizadas exclusivamente para saques autorizados pelo usuário.</li>
                <li>6.3. O usuário é responsável pela confidencialidade de sua conta.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">7. RESPONSABILIDADE DA GAMEODYS E DO USUÁRIO</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>7.1. A Gameodys não garante o funcionamento ininterrupto da plataforma.</li>
                <li>7.2. Não nos responsabilizamos por perdas decorrentes de uso indevido da conta ou falhas externas.</li>
                <li>7.3. A empresa não incentiva o uso irresponsável de GameCoins, Créditos ou recompensas.</li>
                <li>7.4. A Gameodys não tem obrigação de controlar a utilização que os usuários fizerem da plataforma. O uso da plataforma em conformidade com a lei, com este Termo de Uso ou de acordo com os valores médios presumidos da sociedade e a ordem pública é de responsabilidade exclusiva do usuário.</li>
                <li>7.5. A Gameodys também não tem a obrigação de verificar, e não verifica, a identidade dos usuários, nem a veracidade, vigência e autenticidade dos dados fornecidos por eles. A responsabilidade por danos diretos e/ou indiretos decorrentes do uso da plataforma ou de informações falsas é exclusivamente do usuário.</li>
                <li>7.6. <strong>Indenização:</strong> O usuário concorda em isentar e indenizar a Gameodys, suas subsidiárias, afiliadas, executivos, agentes e funcionários de e contra qualquer reclamação de terceiros decorrente ou relacionada à violação de qualquer lei ou direitos de terceiros, ou ao uso da plataforma e/ou jogos, incluindo qualquer responsabilidade ou despesa decorrente de todas as reivindicações, perdas, danos (reais ou consequentes), ações judiciais, julgamentos, custos de litígio e honorários advocatícios de qualquer tipo. Nada nestas condições será interpretado como exclusão ou limitação da sua responsabilidade com relação a qualquer indenização prevista.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">8. CONTRIBUIÇÕES DE IDEIAS OU SUGESTÕES</h2>
              <p className="text-gray-300 font-hero leading-relaxed mb-3">
                8.1. Ao enviar ideias, sugestões, feedbacks ou propostas para a Gameodys, o usuário concorda que:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero ml-6">
                <li>(a) está ciente de que não haverá qualquer compensação obrigatória, participação em lucros ou pagamento de royalties pela utilização dessas contribuições;</li>
                <li>(b) a Gameodys não tem qualquer obrigação de adotar, implementar ou responder às ideias recebidas;</li>
                <li>(c) a eventual adoção de uma sugestão será uma liberalidade da Gameodys, que poderá, por mera cortesia, conceder alguma recompensa ou menção, sem que isso gere vínculo ou expectativa futura;</li>
                <li>(d) todas as contribuições poderão ser utilizadas livremente pela Gameodys para quaisquer fins comerciais, técnicos ou estratégicos, sem qualquer limitação ou obrigação adicional.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">9. SUSPENSÃO E ENCERRAMENTO DE CONTAS</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>9.1. Contas podem ser suspensas em caso de suspeita de violação dos Termos.</li>
                <li>9.2. Confirmada a infração, a conta poderá ser encerrada sem aviso prévio.</li>
                <li>9.3. Em caso de suspeita de atividade ilegal, a empresa poderá comunicar as autoridades competentes.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">10. MODIFICAÇÕES</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>10.1. Este Termo poderá ser alterado a qualquer momento, sendo as mudanças comunicadas por e-mail ou dentro da plataforma.</li>
                <li>10.2. O uso contínuo após alterações será considerado aceitação dos novos termos.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">11. SUPORTE E COMUNICAÇÃO COM O USUÁRIO</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>11.1. Para dúvidas, comentários ou sugestões, entre em contato com a Gameodys pelo e-mail: <a href="mailto:suporte@gameodys.com" className="text-green-primary hover:underline">suporte@gameodys.com</a> ou por outros canais oficiais da plataforma.</li>
                <li>11.2. Sempre faremos o nosso melhor para resolver quaisquer problemas que o usuário tenha com o serviço. Para agilizar o atendimento, pedimos que o usuário inclua o máximo de informações possível: nickname, data e horário do problema, imagens (se aplicável), etc.</li>
                <li>11.3. O prazo padrão de resposta é de até 72 horas úteis após o recebimento da solicitação.</li>
                <li>11.4. Recomendamos que o usuário evite o uso de redes sociais para manifestar descontentamentos ou suspeitas sobre outros usuários. Utilize sempre os canais oficiais da Gameodys.</li>
              </ul>
            </section>
          </TabsContent>

          <TabsContent value="privacy" className="bg-dark-secondary border border-purple-primary/20 rounded-xl p-8 space-y-8">
            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">1. DADOS COLETADOS</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li><strong>Cadastro:</strong> nome, e-mail, senha, data de nascimento;</li>
                <li><strong>Acesso:</strong> IP, dispositivo, horários de login;</li>
                <li><strong>Bancários:</strong> nome, CPF, dados da conta;</li>
                <li><strong>Uso:</strong> partidas, histórico de GameCoins e Créditos, comportamentos.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">2. USO DOS DADOS</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>Execução dos serviços;</li>
                <li>Processamento de transações;</li>
                <li>Prevenção de fraudes;</li>
                <li>Cumprimento de obrigações legais.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">3. ARMAZENAMENTO E SEGURANÇA</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>Dados criptografados e protegidos;</li>
                <li>Sessões temporárias de login por até 7 dias.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">4. COMPARTILHAMENTO</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>Apenas com prestadores autorizados;</li>
                <li>Em cumprimento de obrigações legais;</li>
                <li>Em casos de fusão ou aquisição.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">5. DIREITOS DO USUÁRIO</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>Acesso, correção, exclusão e revogação do consentimento;</li>
                <li>Solicitações devem ser enviadas para <a href="mailto:suporte@gameodys.com" className="text-green-primary hover:underline">suporte@gameodys.com</a>.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">6. VERIFICAÇÃO DE IDENTIDADE</h2>
              <p className="text-gray-300 font-hero leading-relaxed">
                Para atender obrigações legais e comerciais, a Gameodys poderá, durante ou após o cadastro, solicitar verificação de identidade. Isso pode incluir o fornecimento de documentos como RG, CNH, passaporte, comprovantes de residência, chamadas telefônicas ou videoconferências.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold font-archive text-purple-light mb-4">7. ALTERAÇÕES NA POLÍTICA</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 font-hero">
                <li>Serão notificadas por e-mail ou na plataforma;</li>
                <li>O uso continuado implica aceitação das mudanças.</li>
              </ul>
            </section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
